import { roundMoney } from '@renderer/utils/formatCurrency'

interface PaymentResponse {
  order_id: string
  wallet_id?: string
  qr_url?: string // Optional for Snap payments
  snap_token?: string // For Snap payments
  redirect_url?: string // Optional for ToyyibPay redirect payments
  expiry_time: string
  // The amount the gateway actually bills, in `charge_currency`.
  // Midtrans charges in IDR (integer), ToyyibPay charges in MYR (2 decimals).
  amount: number
  // Currency the `amount`/`charge_currency` field is denominated in.
  currency?: 'IDR' | 'MYR'
  /** ISO-4217 currency the customer is charged in (IDR for cross-border QRIS). */
  charge_currency?: string
  /** Project-native amount the price was quoted in (e.g. RM for MY projects). */
  native_amount?: number
  /** Native currency of `native_amount`. */
  native_currency?: string
  /** MYR→IDR rate used for the QRIS charge (present only when converted). */
  fx_rate?: number
  /** "as of" timestamp of `fx_rate`. */
  fx_as_of?: string
  /** Whether `fx_rate` is live or the safety fallback. */
  fx_source?: 'live' | 'fallback'
  server_time: string
  payment_type: 'core' | 'snap' | 'redirect'
  provider?: 'midtrans' | 'toyyibpay' | 'mercadopago'
  client_key?: string // For Snap payments
}

interface PaymentStatusResponse {
  paid: boolean
  serverTime: string
  isExpired: boolean
  transactionStatus: string
}

/**
 * Thrown when the backend rejects a Midtrans/QRIS charge because the
 * project's country is in a voucher-only tier (`localized_in_progress` or
 * `global_hidden`) and the operator hasn't wired a custom gateway. The
 * photobooth's payment page should switch to voucher-only UI on this error
 * rather than retrying.
 */
export class VoucherOnlyRegionError extends Error {
  constructor(public readonly paymentSetupTier?: string) {
    super('Payment region is voucher-only')
    this.name = 'VoucherOnlyRegionError'
  }
}

export class MidtransUtils {
  private static apiUrl = `${process.env.VITE_API_URL}`

  public static async initiatePayment(
    grossAmount: number,
    id: string,
    expiryDuration: number,
    projectId: string,
    filterId: string,
    frameId: string,
    discountAmount: number,
    isExtraPrint: boolean = false,
    provider: 'midtrans' | 'toyyibpay' | 'mercadopago' = 'midtrans',
    isAiEffect: boolean = false
  ): Promise<PaymentResponse> {
    try {
      // Amounts are in the project's native currency; the backend converts to
      // the gateway's settlement currency (and rounds to its unit) itself. We
      // only round to 2 decimals here to strip float noise without dropping
      // cents — an integer round would lose ringgit/dollar cents.
      const finalAmount = roundMoney(Math.max(0, grossAmount), 'MYR')
      const roundedDiscountAmount = roundMoney(Math.max(0, discountAmount), 'MYR')

      const response = await fetch(`${this.apiUrl}/api/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        cache: 'no-cache',
        body: JSON.stringify({
          grossAmount: finalAmount, // Native amount, after discount
          id,
          expiryDuration,
          projectId,
          filterId,
          frameId,
          discountAmount: roundedDiscountAmount,
          isExtraPrint,
          isAiEffect,
          provider
        })
      })

      if (!response.ok) {
        // 409 voucher_only_region: backend refused QRIS for this project's
        // country. Throw a typed error so the caller can switch UI rails
        // without parsing free-text messages.
        if (response.status === 409) {
          let body: { error?: string; paymentSetupTier?: string } | null = null
          try {
            body = await response.json()
          } catch {
            // ignore parse errors; fall through to generic message
          }
          if (body?.error === 'voucher_only_region') {
            throw new VoucherOnlyRegionError(body.paymentSetupTier)
          }
        }
        throw new Error(`Payment initiation failed: ${response.statusText}`)
      }

      const data = await response.json()
      // IDR has no fractional unit, so we round to an integer. MYR is
      // expressed with two decimals (sen), so we must NOT round it down
      // or we'd lose precision (e.g. 9.59 -> 10).
      //
      // `currency` is optional on the response, so we also fall back on
      // `provider` (toyyibpay always bills in MYR, mercadopago in MXN).
      // Casing is normalised because backends and proxies can mutate string
      // casing.
      const declaredCurrency =
        typeof data?.charge_currency === 'string'
          ? data.charge_currency.toUpperCase()
          : typeof data?.currency === 'string'
            ? data.currency.toUpperCase()
            : ''
      const declaredProvider =
        typeof data?.provider === 'string' ? data.provider.toLowerCase() : ''
      // Two-decimal settlement currencies (MYR, MXN, USD-like) must keep
      // their cents; only IDR rounds to a whole integer. Detect by the
      // declared charge currency, with a provider fallback for each gateway.
      const isTwoDecimal =
        declaredCurrency === 'MYR' ||
        declaredCurrency === 'MXN' ||
        declaredProvider === 'toyyibpay' ||
        declaredProvider === 'mercadopago'
      return {
        ...data,
        amount: isTwoDecimal
          ? Math.round((Number(data.amount) || 0) * 100) / 100
          : Math.round(Number(data.amount) || 0)
      }
    } catch (error) {
      console.error('Error initiating payment:', error)
      throw error
    }
  }

  public static async checkPaymentStatus(orderId: string): Promise<PaymentStatusResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/api/payments/${orderId}`, {
        method: 'GET',
        cache: 'no-cache',
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`Payment status check failed: ${response.statusText}`)
      }

      const data = await response.json()
      return {
        paid: data.simplifiedResponse.paid,
        serverTime: data.simplifiedResponse.transaction_time || new Date().toISOString(),
        isExpired: data.simplifiedResponse.isExpired,
        transactionStatus: data.simplifiedResponse.transaction_status
      }
    } catch (error) {
      console.error('Error checking payment status:', error)
      throw error
    }
  }
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    snap: {
      pay
    }
  }
}

export default MidtransUtils
